import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { useDaoConfig } from '@/hooks/useDaoConfig';

import { animateElement, type AnimationType } from './../animations';

import type { WelcomeStepRef } from './types';

export const WelcomeStep1 = forwardRef<WelcomeStepRef>((_, ref) => {
  const { daoName } = useDaoConfig();
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const elements = containerRef.current?.querySelectorAll<HTMLElement>('[data-animate-in]');

    elements?.forEach((element) => {
      const animation = element.dataset.animateIn as AnimationType | undefined;

      if (animation) {
        animateElement(element, animation).catch(() => {});
      }
    });
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      animateOut: async () => {
        const elements = containerRef.current?.querySelectorAll<HTMLElement>('[data-animate-out]');

        if (!elements) return;

        await Promise.all(
          Array.from(elements).map((element) => {
            const animation = element.dataset.animateOut as AnimationType | undefined;

            if (!animation) return Promise.resolve();

            return animateElement(element, animation);
          }),
        );
      },
    }),
    [],
  );

  return (
    <div ref={containerRef} className="stepContainer">
      <h1 data-animate-in="textFadeIn" data-animate-out="textFadeOut" className="opacity-0">
        Working with strangers shouldn't be like a leap of faith.
      </h1>

      <p
        data-animate-in="textFadeIn"
        data-animate-out="textFadeOut"
        className="opacity-0 text-[--text-dark-secondary] font-normal transition-none transition-duration-0 transition-delay-0"
      >
        Most freelance platforms put two strangers together and step back. When the work goes
        sideways, the people involved are mostly on their own.
      </p>

      {/* Image - animations container */}
      <div className="animationsContainer">
        <div className="relative rounded-full overflow-hidden border border-[base-border]">
          <div
            data-animate-out="scaleUp"
            className="h-[167px] aspect-square rounded-full glassEffect"
          />
          <div
            className="
              z-0 blur-[12px] absolute 
              top-[73%] -translate-y-[50%] 
              left-[73%] -translate-x-[50%]
              h-[50px] w-[50px] 
              rounded-full bg-[--colors-emerald-500]
            "
          />
          <div
            className="
              z-0 blur-[12px] absolute 
              top-[27%] -translate-y-[50%]
              left-[27%] -translate-x-[50%]
              h-[50px] w-[50px] 
              rounded-full bg-[--colors-cyan-600]
            "
          />
        </div>
      </div>

      <h1 data-animate-in="textFadeIn" data-animate-out="textFadeOut" className="opacity-0 ">
        <span className="text-[--state-brand-active]">{daoName}</span> exists so trust doesn't have to
        be a gamble.
      </h1>
    </div>
  );
});

WelcomeStep1.displayName = 'WelcomeStep1';
